/**
 * IXVerify v2 — shared badges/chips (ixv2 WP-2.3).
 *
 * Status-pill recipe from the design system: color never carries meaning
 * alone — every badge pairs its hue with an icon and/or explicit text.
 */
import { Cloud, FileSpreadsheet, Link2, PencilLine, ShieldAlert } from 'lucide-react';
import type { SubjectSource } from '../../lib/api';

const pillBase =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium';

/** Provenance badge for a roster row: crio | manual | csv. */
export function SourceBadge({ source }: { source: SubjectSource | string }) {
  switch (source) {
    case 'crio':
      return (
        <span className={`${pillBase} bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300`}>
          <Cloud className="h-3 w-3" aria-hidden="true" />
          CRIO
        </span>
      );
    case 'csv':
      return (
        <span className={`${pillBase} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}>
          <FileSpreadsheet className="h-3 w-3" aria-hidden="true" />
          CSV
        </span>
      );
    default:
      return (
        <span className={`${pillBase} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}>
          <PencilLine className="h-3 w-3" aria-hidden="true" />
          Manual
        </span>
      );
  }
}

/**
 * §3.4 pending-confirmation chip: a manual/CSV row whose DOB entry an admin
 * has not yet verified. Reflects SERVER state only (R5) — the chip renders
 * from `pending_confirmation` and clears only after the server confirms.
 */
export function PendingConfirmationChip() {
  return (
    <span className={`${pillBase} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`}>
      <ShieldAlert className="h-3 w-3" aria-hidden="true" />
      Pending confirmation
    </span>
  );
}

/** Subject study-status pill (screening/enrolled/… — free text, quiet neutral). */
export function SubjectStatusPill({ status }: { status: string }) {
  const cls =
    status === 'enrolled'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : status === 'discontinued'
      ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  return <span className={`${pillBase} ${cls} capitalize`}>{status}</span>;
}

/**
 * Registry-link chip (ported from v1 StudyManagement). Renders purely from
 * study row data — v1's registry-link endpoints were deleted in WP-0.2 and
 * return gated in WP-3.1, so this chip must never depend on a live link
 * endpoint.
 */
export function RegistryLinkChip({
  registryProtocolId,
  registryProtocol,
}: {
  registryProtocolId?: string | null;
  registryProtocol?: { protocol_number?: string | null; study_nickname?: string | null } | null;
}) {
  if (registryProtocolId) {
    return (
      <span
        className={`${pillBase} bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300`}
        title={registryProtocol?.study_nickname || undefined}
      >
        <Link2 className="h-3 w-3" aria-hidden="true" />
        Registry: {registryProtocol?.protocol_number || 'linked'}
      </span>
    );
  }
  return (
    <span className={`${pillBase} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`}>
      Not linked
    </span>
  );
}

/**
 * Session status pill (WP-3.3 — Dashboard/History). One recipe for every
 * `ixv_sessions.status`; a dot + explicit label (color never alone).
 */
const SESSION_STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  staged: { label: 'Ready to dispense', cls: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' },
  capturing: { label: 'Capturing', cls: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' },
  review: { label: 'In review', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  completed: { label: 'Published', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  second_check_pending: { label: 'Second check pending', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  verified: { label: 'Verified', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  mismatch_recorded: { label: 'Mismatch recorded', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
};

export function SessionStatusPill({ status }: { status: string }) {
  const style = SESSION_STATUS_STYLES[status] || {
    label: status,
    cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span className={`${pillBase} ${style.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {style.label}
    </span>
  );
}
